import { Injectable, inject } from '@angular/core';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

@Injectable({ providedIn: 'root' })
export class LoggerService {
  // Check if we're in production mode
  private readonly isProduction = typeof window !== 'undefined' 
    ? (window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1'))
    : false;
  private readonly minLevel = this.isProduction ? LogLevel.WARN : LogLevel.DEBUG;

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  error(message: string, error?: unknown, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorDetails = this.formatError(error);
      console.error(`[ERROR] ${message}`, errorDetails, ...args);
      
      // In production, send to error tracking service
      if (this.isProduction && error) {
        this.sendToErrorTracking(message, error);
      }
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  private formatError(error: unknown): unknown {
    if (error instanceof Error) {
      return {
        message: error.message,
        stack: error.stack,
        name: error.name,
      };
    }
    if (typeof error === 'object' && error !== null) {
      return error;
    }
    return String(error);
  }

  private sendToErrorTracking(message: string, error: unknown): void {
    // TODO: Integrate with error tracking service (Sentry, LogRocket, etc.)
    // Example:
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: { message } });
    // }
  }
}
